import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { format } from 'date-fns';
import { Icons, PageTitle, Card, Button, CurrencyInput } from '../shared/ui';
const {
  DollarSign: CurrencyDollarIcon, CheckCircle2: CheckCircleIcon, Clock: ClockIcon,
  Alert: ExclamationTriangleIcon, Plus: PlusIcon, X: XMarkIcon, Wallet: BanknotesIcon,
  CreditCard: CreditCardIcon, Document: DocumentTextIcon, Image: PhotoIcon,
  Trash: TrashIcon, Eye: EyeIcon, MessageSquare: ChatBubbleLeftRightIcon, Lock: LockClosedIcon,
} = Icons;

const RenterPayments = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['finance', 'common']);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [conversationData, setConversationData] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);

  // Fetch renter's payments
  const { data: payments = [], isLoading, error } = useQuery({
    queryKey: ['renter-payments'],
    queryFn: async () => {
      const response = await api.get('/api/renters/payments');
      return response.data;
    },
    select: (data) => Array.isArray(data) ? data : []
  });

  // Fetch current month invoice
  const { data: invoice } = useQuery({
    queryKey: ['renter-invoice', selectedMonth],
    queryFn: async () => {
      const response = await api.get(`/api/renters/invoices/${selectedMonth}`);
      return response.data;
    },
  });

  // Submit payment transaction mutation
  const submitPaymentMutation = useMutation({
    mutationFn: async (paymentData) => {
      const { paymentId, ...transactionData } = paymentData;
      if (paymentId) {
        // Submit to existing payment
        const response = await api.post(`/api/renters/payments/${paymentId}/transactions`, transactionData);
        return response.data;
      } else {
        // Create new payment with transaction
        const response = await api.post('/api/renters/payments/submit', transactionData);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renter-payments'] });
      queryClient.invalidateQueries({ queryKey: ['renter-invoice'] });
      setShowSubmitModal(false);
      // Refresh conversation if viewing
      if (selectedPayment) {
        fetchConversation(selectedPayment.id);
      }
    },
  });

  // Delete transaction mutation
  const deleteTransactionMutation = useMutation({
    mutationFn: async (transactionId) => {
      const response = await api.delete(`/api/renters/payments/transactions/${transactionId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renter-payments'] });
      queryClient.invalidateQueries({ queryKey: ['renter-invoice'] });
      // Refresh conversation if viewing
      if (selectedPayment) {
        fetchConversation(selectedPayment.id);
      }
    },
  });

  // Fetch conversation for a payment
  const fetchConversation = async (paymentId) => {
    setLoadingConversation(true);
    try {
      const response = await api.get(`/api/renters/payments/${paymentId}/conversation`);
      setConversationData(response.data);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      setConversationData(null);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Open conversation modal
  const openConversation = async (payment) => {
    setSelectedPayment(payment);
    setShowConversationModal(true);
    await fetchConversation(payment.id);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Confirmed' },
      partial: { color: 'bg-teal-100 text-teal-800', icon: BanknotesIcon, label: 'Partial' },
      rejected: { color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon, label: 'Rejected' },
      overdue: { color: 'bg-red-100 text-red-800', icon: ExclamationTriangleIcon, label: 'Overdue' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount || 0);
  };

  // Generate month options for last 6 months
  const monthOptions = [];
  for (let i = 0; i < 6; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    monthOptions.push(format(date, 'yyyy-MM'));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageTitle
        title={t('finance:renterView.title')}
        subtitle={t('finance:renterView.subtitle')}
        actions={
          <Button kind="primary" iconLeft={<Icons.Plus size={16} />} onClick={() => setShowSubmitModal(true)}>
            {t('finance:renterView.newPayment')}
          </Button>
        }
      />

      {/* Current Month Summary */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 text-teal-600 rounded-xl">
              <CurrencyDollarIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-stone-900">Current Month Summary</h3>
              <p className="text-sm text-stone-500">Track your payment status and remaining balance</p>
            </div>
          </div>
          <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="pl-4 pr-10 py-2 bg-white/50 border border-stone-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm font-medium text-stone-700 shadow-sm transition-all hover:bg-white"
            >
              {monthOptions.map((month) => (
                <option key={month} value={month}>
                  {format(new Date(month + '-01'), 'MMMM yyyy')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {invoice && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-stone-50 to-white rounded-2xl p-5 border border-stone-100 shadow-sm">
              <p className="text-sm font-medium text-stone-500 mb-1">Total Due</p>
              <p className="text-3xl font-bold text-stone-900 tracking-tight">{formatCurrency(invoice.total_amount)}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-5 border border-emerald-100 shadow-sm">
              <p className="text-sm font-medium text-emerald-600 mb-1">Paid Amount</p>
              <p className="text-3xl font-bold text-emerald-700 tracking-tight">{formatCurrency(invoice.paid_amount)}</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-5 border border-amber-100 shadow-sm">
              <p className="text-sm font-medium text-amber-600 mb-1">Remaining</p>
              <p className="text-3xl font-bold text-amber-700 tracking-tight">{formatCurrency(invoice.remaining_amount)}</p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-5 border border-teal-100 shadow-sm flex flex-col justify-center">
              <p className="text-sm font-medium text-teal-600 mb-2">Status</p>
              <div>{getStatusBadge(invoice.status)}</div>
            </div>
          </div>
        )}

        {!invoice && (
          <div className="text-center py-12 rounded-2xl bg-stone-50 border border-dashed border-stone-200">
            <DocumentTextIcon className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 font-medium">No invoice found for this month</p>
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-stone-100 flex items-center gap-3">
          <div className="p-2 bg-teal-100 text-teal-600 rounded-lg">
            <DocumentTextIcon className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold text-stone-900">Payment History</h3>
        </div>
        <div className="overflow-x-auto">
          {payments && payments.length > 0 ? (
            <table className="min-w-full divide-y divide-stone-100">
              <thead className="bg-stone-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Total Due
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-stone-100">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-teal-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-stone-900">
                      {payment.month ? format(new Date(payment.month + '-01'), 'MMMM yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-600">
                      {formatCurrency(payment.total_amount || payment.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-emerald-600">
                      {formatCurrency(payment.paid_amount || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openConversation(payment)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-100 rounded-lg hover:bg-teal-200 transition-colors"
                          title="View Transactions"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1.5" />
                          View
                        </button>
                        {payment.status !== 'confirmed' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowSubmitModal(true);
                            }}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
                            title="Add Transaction"
                          >
                            <PlusIcon className="h-4 w-4 mr-1.5" />
                            Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-16">
              <CurrencyDollarIcon className="h-16 w-16 text-stone-200 mx-auto mb-4" />
              <p className="text-stone-500 font-medium">No payment history found</p>
              <p className="text-sm text-stone-400 mt-1">Submit your first payment to see it here</p>
            </div>
          )}
        </div>
      </div>

      {/* Submit Payment Modal */}
      {showSubmitModal && (
        <PaymentSubmitModal
          onClose={() => {
            setShowSubmitModal(false);
            setSelectedPayment(null);
          }}
          onSubmit={submitPaymentMutation.mutate}
          isLoading={submitPaymentMutation.isPending}
          error={submitPaymentMutation.error}
          invoice={invoice}
          monthOptions={monthOptions}
          selectedPayment={selectedPayment}
        />
      )}

      {/* Conversation Modal */}
      {showConversationModal && (
        <PaymentConversationModal
          payment={selectedPayment}
          conversationData={conversationData}
          loading={loadingConversation}
          onClose={() => {
            setShowConversationModal(false);
            setSelectedPayment(null);
            setConversationData(null);
          }}
          onDelete={(transactionId) => {
            if (window.confirm('Are you sure you want to delete this transaction?')) {
              deleteTransactionMutation.mutate(transactionId);
            }
          }}
          deleteLoading={deleteTransactionMutation.isPending}
          deleteError={deleteTransactionMutation.error}
          onAddTransaction={() => {
            setShowConversationModal(false);
            setShowSubmitModal(true);
          }}
          formatCurrency={formatCurrency}
          getStatusBadge={getStatusBadge}
        />
      )}
    </div>
  );
};

// Payment Submit Modal Component
const PaymentSubmitModal = ({ onClose, onSubmit, isLoading, error, invoice, monthOptions, selectedPayment }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    month: selectedPayment?.month || format(new Date(), 'yyyy-MM'),
    amount: '',
    payment_method: 'bank_transfer',
    notes: '',
  });
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let proofUrl = null;

    // Upload proof image if selected
    if (proofFile) {
      setUploadingProof(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', proofFile);
        const uploadResponse = await api.post('/api/upload', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        proofUrl = uploadResponse.data.url || uploadResponse.data.file_url;
      } catch (uploadErr) {
        console.error('Failed to upload proof:', uploadErr);
        alert('Failed to upload payment proof. Please try again.');
        setUploadingProof(false);
        return;
      }
      setUploadingProof(false);
    }

    onSubmit({
      paymentId: selectedPayment?.id,
      month: formData.month,
      submitted_amount: parseFloat(formData.amount),
      proof_url: proofUrl,
      renter_notes: formData.notes,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProofFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProofFile = () => {
    setProofFile(null);
    setProofPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Pre-fill remaining amount when invoice or selectedPayment is available
  React.useEffect(() => {
    if (selectedPayment && selectedPayment.total_amount && selectedPayment.paid_amount !== undefined) {
      const remaining = selectedPayment.total_amount - (selectedPayment.paid_amount || 0);
      if (remaining > 0 && !formData.amount) {
        setFormData((prev) => ({ ...prev, amount: remaining.toString() }));
      }
    } else if (invoice && invoice.remaining_amount > 0 && !formData.amount) {
      setFormData((prev) => ({ ...prev, amount: invoice.remaining_amount.toString() }));
    }
  }, [invoice, selectedPayment]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount || 0);
  };

  const remainingAmount = selectedPayment
    ? (selectedPayment.total_amount || 0) - (selectedPayment.paid_amount || 0)
    : invoice?.remaining_amount || 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border border-stone-200 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in-up p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {selectedPayment ? 'Add Payment Transaction' : 'Submit Payment'}
          </h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {selectedPayment && (
          <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-md">
            <p className="text-sm text-teal-700">
              <strong>Payment for:</strong> {format(new Date(selectedPayment.month + '-01'), 'MMMM yyyy')}
            </p>
            <p className="text-sm text-teal-700">
              <strong>Remaining:</strong> {formatCurrency(remainingAmount)}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error.response?.data?.detail || 'Failed to submit payment'}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!selectedPayment && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Payment Month</label>
              <select
                name="month"
                value={formData.month}
                onChange={handleChange}
                className="w-full border border-stone-300 rounded-md px-3 py-2"
                required
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>
                    {format(new Date(month + '-01'), 'MMMM yyyy')}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Amount (VND)</label>
            <CurrencyInput
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              className="w-full border border-stone-300 rounded-md px-3 py-2"
              placeholder="Enter payment amount"
              required
            />
            {remainingAmount > 0 && (
              <p className="text-xs text-stone-500 mt-1">
                Remaining balance: {formatCurrency(remainingAmount)}
              </p>
            )}
          </div>

          {/* Payment Proof Upload */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Payment Proof (Screenshot/Image)
            </label>
            {proofPreview ? (
              <div className="relative border border-stone-300 rounded-md p-2">
                <img
                  src={proofPreview}
                  alt="Payment proof preview"
                  className="max-h-40 mx-auto rounded"
                />
                <button
                  type="button"
                  onClick={removeProofFile}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-stone-300 rounded-md p-4 text-center cursor-pointer hover:border-teal-400"
              >
                <PhotoIcon className="h-8 w-8 mx-auto text-stone-400" />
                <p className="text-sm text-stone-500 mt-1">Click to upload payment proof</p>
                <p className="text-xs text-stone-400">JPG, PNG up to 5MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes (Optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full border border-stone-300 rounded-md px-3 py-2"
              rows={2}
              placeholder="Any additional notes for admin"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-stone-300 rounded-md text-stone-700 hover:bg-stone-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isLoading || uploadingProof}
            >
              {isLoading || uploadingProof ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {uploadingProof ? 'Uploading...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  {selectedPayment ? 'Add Transaction' : 'Submit Payment'}
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded-md">
          <p className="text-xs text-teal-700">
            <strong>Note:</strong> Your payment will be reviewed by the admin. Once confirmed,
            it will be reflected in your payment history. Confirmed transactions cannot be deleted.
          </p>
        </div>
      </div>
    </div>
  );
};

// Payment Conversation Modal Component
const PaymentConversationModal = ({
  payment,
  conversationData,
  loading,
  onClose,
  onDelete,
  deleteLoading,
  deleteError,
  onAddTransaction,
  formatCurrency,
  getStatusBadge,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const getTransactionStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      confirmed: { color: 'bg-green-100 text-green-800', label: 'Confirmed' },
      partial: { color: 'bg-teal-100 text-teal-800', label: 'Partial' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border border-stone-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col animate-fade-in-up">
        {/* Header */}
        <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-white/50">
          <div>
            <h2 className="text-xl font-semibold flex items-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 mr-2 text-teal-600" />
              Payment Transactions
            </h2>
            {payment && (
              <p className="text-sm text-stone-500 mt-1">
                {format(new Date(payment.month + '-01'), 'MMMM yyyy')} - {getStatusBadge(payment.status)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Summary */}
        {conversationData?.payment && (
          <div className="p-4 bg-stone-50 border-b border-stone-200">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-stone-500">Total Due</p>
                <p className="text-lg font-bold text-stone-900">
                  {formatCurrency(conversationData.payment.total_amount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Paid</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(conversationData.payment.paid_amount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-500">Remaining</p>
                <p className="text-lg font-bold text-yellow-600">
                  {formatCurrency(
                    (conversationData.payment.total_amount || 0) -
                    (conversationData.payment.paid_amount || 0)
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              <span className="ml-2 text-stone-500">Loading transactions...</span>
            </div>
          ) : conversationData?.transactions?.length > 0 ? (
            <div className="space-y-4">
              {conversationData.transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className={`border rounded-lg p-4 ${transaction.is_locked
                      ? 'border-green-200 bg-green-50'
                      : 'border-stone-200'
                    }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getTransactionStatusBadge(transaction.status)}
                        {transaction.is_locked && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-stone-100 text-stone-700">
                            <LockClosedIcon className="h-3 w-3 mr-1" />
                            Locked
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-stone-500">Submitted:</span>{' '}
                          <span className="font-medium">{formatCurrency(transaction.submitted_amount)}</span>
                        </div>
                        {transaction.confirmed_amount !== null && (
                          <div>
                            <span className="text-stone-500">Confirmed:</span>{' '}
                            <span className="font-medium text-green-600">
                              {formatCurrency(transaction.confirmed_amount)}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-stone-500">Date:</span>{' '}
                          <span>
                            {format(new Date(transaction.submitted_at), 'MMM d, yyyy HH:mm')}
                          </span>
                        </div>
                        {transaction.reviewed_at && (
                          <div>
                            <span className="text-stone-500">Reviewed:</span>{' '}
                            <span>
                              {format(new Date(transaction.reviewed_at), 'MMM d, yyyy HH:mm')}
                            </span>
                          </div>
                        )}
                      </div>
                      {transaction.renter_notes && (
                        <div className="mt-2 p-2 bg-teal-50 rounded text-sm">
                          <span className="text-stone-500">Your note:</span>{' '}
                          {transaction.renter_notes}
                        </div>
                      )}
                      {transaction.admin_note && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
                          <span className="text-stone-500">Admin note:</span>{' '}
                          {transaction.admin_note}
                        </div>
                      )}
                    </div>

                    {/* Proof Image */}
                    <div className="ml-4 flex flex-col items-end gap-2">
                      {transaction.proof_url && (
                        <img
                          src={transaction.proof_url}
                          alt="Payment proof"
                          className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                          onClick={() => setSelectedImage(transaction.proof_url)}
                        />
                      )}
                      {/* Delete button - only show if not locked and pending */}
                      {!transaction.is_locked && transaction.status === 'pending' && (
                        <button
                          onClick={() => onDelete(transaction.id)}
                          disabled={deleteLoading}
                          className="text-red-600 hover:text-red-800 text-xs flex items-center"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      )}
                      {transaction.is_locked && (
                        <span className="text-xs text-stone-400">Cannot delete</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ChatBubbleLeftRightIcon className="h-12 w-12 text-stone-400 mx-auto mb-4" />
              <p className="text-stone-500">No transactions yet</p>
              <p className="text-sm text-stone-400 mt-1">Submit a payment to start the conversation</p>
            </div>
          )}

          {deleteError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">
                {deleteError.response?.data?.detail || 'Failed to delete transaction'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-stone-200 flex justify-between items-center">
          <p className="text-xs text-stone-500">
            <LockClosedIcon className="h-3 w-3 inline mr-1" />
            Locked transactions cannot be deleted
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-stone-300 rounded-md text-stone-700 hover:bg-stone-50"
            >
              Close
            </button>
            {payment?.status !== 'confirmed' && (
              <button
                onClick={onAddTransaction}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                Add Transaction
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-[90vh] p-4">
            <img
              src={selectedImage}
              alt="Payment proof full"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-stone-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenterPayments;
